// This file is shared for SSR and CSR.  The functions define the async calls for getting data
import axios from 'axios';
import axiosRetry from 'axios-retry';
import safeStringify from 'json-stringify-safe';
import DOMPurify from 'isomorphic-dompurify';
import {
  RETRY_COUNT,
  catalogSliceInitialState,
  catalogSliceName,
  SEARCH_API, FILTER_API, ROUTER_BASENAME, ITEM_TYPE_QUIZ, CLIENT_CONTEXT_CHANNEL,
  LIST_ID_FOLLOWED,
  OBJECT_TYPE_COURSE,
  LIST_API_HOST,
} from '../common/constants';
import logger from '../common/logger';

import { ApiTimer } from '../common/timers';
import XHRError from '../common/xhrError';
import {
  calculateEffortDisplay, getCourseAPIHost, getEnvironmentConstant, shuffleArray, flatDeep,
  chunkArray,
  getEnrollmentSlug,
} from '../common/utilities';
import { getChildProperty } from '../common/object';
// import curatedData from '../../mocks/curatedData.json';

axiosRetry(axios, { retries: RETRY_COUNT });

const headers = {
  'Content-Type': 'application/json',
};

// Returns a promise that resolves user data
export const getDataState = ({
  params, env, isPreview, apiVersion = 'v1',
}) => {
  const host = getCourseAPIHost({ env, isPreview });
  let dataFetchURL = '';
  if (params.curated || params.course) {
    const slug = params.curated ? params.curated : params.course;
    dataFetchURL = `${host}${getEnvironmentConstant({ key: ROUTER_BASENAME, env })}/api/curated/${apiVersion}/${encodeURIComponent(decodeURIComponent(slug))}`;
  } else if (params.tutorial || params.module) {
    const slug = params.tutorial ? params.tutorial : params.module;
    dataFetchURL = `${host}${getEnvironmentConstant({ key: ROUTER_BASENAME, env })}/api/tutorial/${apiVersion}/${encodeURIComponent(decodeURIComponent(slug))}`;
  } else {
    dataFetchURL = `${host}${getEnvironmentConstant({ key: ROUTER_BASENAME, env })}/api/collection/${apiVersion}/${encodeURIComponent(decodeURIComponent(params.collectionSlug))}`;
  }
  logger.debug('\n\nData State Fetch HOST:  ', host);

  logger.debug(`\n\nData State Fetch URL: (${isPreview ? 'PREVIEW' : 'PUBLISHED'})  `, dataFetchURL);

  const dataPromiseStart = new Date().getTime();

  let dataPromiseTime;
  const dataPromise = new Promise((resolve, reject) => {
    const dataTimer = new ApiTimer();
    dataTimer.set(dataFetchURL);

    axios
      .get(dataFetchURL, { headers: { ...headers } })
      .catch((err) => {
        dataTimer.clear();
        const xhrErr = new XHRError(err?.response?.status || 500, err.message);
        logger.error(`Error in getDataState (catch1) ${err}`);
        reject(xhrErr);
      })
      .then((payload) => {
        dataTimer.clear();
        dataPromiseTime = new Date().getTime() - dataPromiseStart;
        logger.debug(`Timing:\nCourse Data call: ${dataPromiseTime}ms`);

        // Validate requested tutorial exists in curated list
        if (params.curated && params.tutorialId) {
          const isValidTutorial = payload?.data?.tutorials && payload.data.tutorials.find((t) => t.id === params.tutorialId) !== undefined;

          if (!isValidTutorial) {
            const message = `Error in getDataState invalid tutorialId (${params.tutorialId}) specified for curated list ${params.curated}`;
            const xhrErr = new XHRError(404, message);
            logger.error(message);
            reject(xhrErr);
          }
        }

        // TODO: Find all non graded problems and check for question/answer randomization per https://wiki.autodesk.com/display/CredSys/Content+Data+Models#ContentDataModels-VideoDataModel
        if (payload?.data?.unitLookup) {
          const { unitLookup } = payload.data;
          Object.keys(unitLookup).forEach((unitId) => {
            // Shuffle questions if randomizeProblems set
            unitLookup[unitId].content
              ?.filter((c) => c.type === ITEM_TYPE_QUIZ && !c.graded && c.randomizeProblems)
              ?.forEach((quizToRandomize) => {
                shuffleArray(quizToRandomize.questions);
              });

            // Shuffle answers if randomize set
            unitLookup[unitId].content
              ?.filter((c) => c.type === ITEM_TYPE_QUIZ && !c.graded)
              ?.forEach((quizToRandomize) => {
                quizToRandomize?.questions?.forEach((q) => {
                  if (q.randomize) {
                    shuffleArray(q.answers);
                  }
                });
              });
          });
        }

        resolve({ ...payload?.data });
      })

      .catch((err) => {
        dataTimer.clear();
        logger.error(`Error in getDataState (catch2) ${err}`);
        reject(err);
      });
  });
  return dataPromise;
};
/*
export const deriveCombinedFiltersFromString = ({ filters = [], presetFilterString }) => {
  const derivedFilters = [...filters];
  if (presetFilterString) {
    // console.log('\n\n\n presetFilterString: ', presetFilterString);
    const nonFilters = ['page', 'sort', 'term'];
    const splitFilters = presetFilterString.split('/');
    let filterKeys = '';
    let filterValues = '';
    switch (splitFilters.length) {
    case 1: break;
    case 2: [filterKeys, filterValues] = splitFilters; break;
    case 3: [, filterKeys, filterValues] = splitFilters; break;
    default: break;
    }

    if (filterKeys.length && filterValues.length) {
      const pipeDelimeter = filterKeys.includes('%7C') ? '%7C' : '|';
      const filterKeyList = filterKeys.split(pipeDelimeter).map((key) => decodeURIComponent(key));
      const filterValuesList = filterValues.split(pipeDelimeter).map((val) => decodeURIComponent(val));
      filterKeyList.forEach((key, idx) => {
        if (!nonFilters.includes(key.toLowerCase())) {
          const selectedFilter = derivedFilters.find((filter) => filter.id === key);
          if (selectedFilter) {
            filterValuesList[idx].split(',').forEach((value) => {
              selectedFilter.selections.find((selection) => selection.value === value).selected = true;
            });
          }
        }
      });
    }
  }

  return derivedFilters;
};
*/
export const parseFilterSetFromString = ({ catalogResult, payload }) => {
  const { presetFilterString } = payload;
  const requestBody = {
    page: catalogResult.page ? catalogResult.page - 1 : catalogResult.page,
    source: 'Learn ONE',
  };

  if (catalogResult.term) {
    requestBody.term = catalogResult.term;
  }

  if (catalogResult.sort) {
    requestBody.sort = catalogResult.sort;
  }

  if (catalogResult?.filters?.length || catalogResult?.filterGroups?.length) {
    const filters = {};

    const getSelected = (filter) => {
      const selected = filter?.selections?.filter((selection) => selection.selected).map((selection) => selection.value);
      if (selected.length) {
        filters[filter.id] = selected;
      }
    };
    catalogResult?.filters?.forEach((filter) => { getSelected(filter); });
    catalogResult?.filterGroups?.forEach((filterGroup) => {
      filterGroup?.filters?.forEach((filter) => { getSelected(filter); });
    });

    if (Object.keys(filters).length > 0) {
      requestBody.filters = filters;
    }
  }

  const presets = { };

  if (presetFilterString) {
    const decodedFilterString = decodeURI(presetFilterString).replace(/%7C/g, '|');
    // console.log('\n\n\n decodedFilterString: ', decodedFilterString);
    const nonFilters = ['page', 'sort', 'term'];
    const splitFilters = decodedFilterString.split('/');
    let filterKeys = '';
    let filterValues = '';
    let term = '';
    switch (splitFilters.length) {
    case 1: [term] = splitFilters; break;
    case 2: [filterKeys, filterValues] = splitFilters; break;
    case 3: [term, filterKeys, filterValues] = splitFilters; break;
    default: break;
    }
    // console.log('\n\n\n presetFilterString t/k/v: ', term, filterKeys, filterValues);
    if (term !== '') {
      presets.term = decodeURIComponent(term);
    }
    if (filterKeys.length && filterValues.length) {
      const filterKeyList = filterKeys.split('|').map((key) => decodeURIComponent(key));
      const filterValuesList = filterValues.split('|').map((val) => decodeURIComponent(val));
      filterKeyList.forEach((key, idx) => {
        if (!nonFilters.includes(key.toLowerCase())) {
          if (!presets.filters) {
            presets.filters = {};
          }
          presets.filters[key] = filterValuesList[idx].split(',');
        } else if (key === 'page') {
          presets[key] = parseInt(filterValuesList[idx], 10) - 1;
        } else {
          presets[key] = filterValuesList[idx];
        }
      });
    }
  }

  const combinedFilters = { ...requestBody, ...presets };

  // Handle XSS Security and encoding.
  if (combinedFilters.term) {
    combinedFilters.term = encodeURIComponent(DOMPurify.sanitize(combinedFilters.term));
  }
  // console.log('\n\n\n presets: ', presets, JSON.stringify(presets));
  return combinedFilters;
};

export const getSearchResult = async ({
  catalogResult, presetFilterString, env, appVersion, perPage = 24, filterHistory = {},
}) => {
  logger.info('Learn Catalog - getSearchResult: ', catalogResult, presetFilterString);
  const requestBody = { ...parseFilterSetFromString({ catalogResult, payload: { presetFilterString, env } }), perPage };
  const updatedFilterHistory = { ...filterHistory };
  logger.info('Catalog - Starting getSearchResult ');
  const startTime = new Date();

  const cardRemapper = (card) => {
    const retCard = { ...card };

    if (retCard.effort) {
      retCard.effort = calculateEffortDisplay(card.effort);
    }
    if (retCard.moduleType) {
      retCard.type = retCard.moduleType;
    }
    if (!retCard.isStandAlone && retCard.relatedCourses) {
      retCard.relatedCurateds = retCard.relatedCourses;
    }

    if (retCard.isStandAlone && retCard.relatedCurateds) {
      delete retCard.relatedCurateds;
    }

    retCard.meta = {};
    if (retCard.level) {
      retCard.meta.level = retCard.level;
    }
    if (retCard.type && retCard.type[0]) {
      [retCard.meta.type] = retCard.type;
    }

    delete retCard.relatedCourses;
    delete retCard.moduleType;
    return retCard;
  };

  const historyKeys = [];
  const dataPromises = [];

  // Call service
  logger.info(`${appVersion} - Catalog - Pushing search call into promise array: ${getEnvironmentConstant({ key: SEARCH_API, env })} - ${safeStringify(requestBody)}`);

  dataPromises.push(
    axios.post(getEnvironmentConstant({ key: SEARCH_API, env }), requestBody, { headers: { accept: 'application/json', 'X-GnosisVersion': appVersion } })
      .catch((err) => {
        logger.error('Catalog - Caught error in search call', err);
        return {
          data: {
            status: 'Error',
            errorCode: err.errorCode || 500,
            errorMessage: err.message,
            page: 0,
          },
        };
      })
      .then((res) => {
        logger.info(`Catalog - Successful search call - ${safeStringify(res)}`);
        const { data: retData } = res;

        if (retData.cards) {
          retData.cards = retData.cards.map(cardRemapper);
        }

        if (requestBody.sort) {
          retData.sort = requestBody.sort;
        }

        // retData.filters = catalogResult.filters;
        // Convert from 0 based array to 1 based list

        retData.page += 1;
        retData.term = retData.term ? decodeURIComponent(retData.term) : catalogResult.term;
        logger.info(`Catalog - Successful search call returning data - ${safeStringify(retData)}`);
        return retData;
      }),
  );

  if (!Object.hasOwnProperty.call(filterHistory, '{}')) {
    historyKeys.push('{}');
    dataPromises.push(
      axios.post(getEnvironmentConstant({ key: FILTER_API, env }), { source: 'Learn ONE' }, { headers: { accept: 'application/json', 'X-GnosisVersion': appVersion } })
        .catch((err) => {
          logger.error(`Catalog - Filter API call failed for ${getEnvironmentConstant({ key: FILTER_API, env })} - ${safeStringify(err)}`);
          return {
            data: {
              errorCode: err.errorCode || 500,
              errorMessage: err.message,
            },
          };
        })
        .then((res) => ({ ...res.data, filters: res.data?.filters?.map((filter) => ({ selections: [], ...filter })) || [] })),
    );
  }

  // Multiple filters are selected so we need to ensure the filter sets for each individual selection only exist in the filter history
  // This is also needed for a single filter and a request term
  if ((requestBody.filters && Object.keys(requestBody.filters).length > 0) || (requestBody.filters && Object.keys(requestBody.filters).length === 1 && requestBody.term && requestBody.term !== '')) {
    Object.keys(requestBody.filters).forEach((filterKey) => {
      const reducedFilterSet = { ...requestBody.filters };
      delete reducedFilterSet[filterKey];
      historyKeys.push(JSON.stringify(reducedFilterSet) + requestBody.term);
      dataPromises.push(
        axios.post(getEnvironmentConstant({ key: SEARCH_API, env }), {
          ...requestBody, filters: reducedFilterSet, perPage: 1,
        }, { headers: { accept: 'application/json', 'X-GnosisVersion': appVersion } })
          .catch((err) => {
            logger.info('Catalog - Caught error in search call', err);
            return {
              data: {
                status: 'Error',
                errorCode: err.errorCode || 500,
                errorMessage: err.message,
                page: 0,
              },
            };
          }).then((res) => (res.data)),
      );
    });
  }

  const data = await Promise.all(dataPromises).then(([results, ...filterData]) => {
    while (filterData?.length > 0) {
      const filterValue = filterData.pop();
      const historyKey = historyKeys.pop();
      updatedFilterHistory[historyKey] = { filters: filterValue.filters, filterGroups: filterValue.filterGroups };
    }

    // Merge default set for use with search term results
    if (results.term && results.term !== '' && (!requestBody.filters || Object.keys(requestBody.filters).length === 0)) {
      const newBaseHistory = {};
      const filterMapper = (filter) => {
        const resultFilter = results?.filters?.find((resFilter) => resFilter.id === filter.id);
        return {
          ...filter,
          selections: [...filter.selections.map((selection) => {
            const resultSelection = resultFilter?.selections?.find((sel) => sel.value === selection.value);
            if (resultSelection) {
              return resultSelection;
            }
            return { ...selection, count: 0 };
          })],
        };
      };
      newBaseHistory.filters = updatedFilterHistory[`{}${requestBody.term}`]?.filters?.map(filterMapper);
      newBaseHistory.filterGroups = updatedFilterHistory[`{}${requestBody.term}`]?.filterGroups?.map((filterGroup) => ({
        ...filterGroup,
        filters: filterGroup.filters.map(filterMapper),
      }));
      updatedFilterHistory[`{}${requestBody.term}`] = newBaseHistory;
    }

    if (results?.status === 'Completed') {
      if (requestBody.filters && !Object.hasOwnProperty.call(updatedFilterHistory, JSON.stringify(requestBody.filters))) {
        updatedFilterHistory[JSON.stringify(requestBody.filters) + requestBody.term] = { filters: results.filters, filterGroups: results.filterGroups };
      }

      if ((requestBody.filters && Object.keys(requestBody.filters).length > 0) || requestBody.term) {
        const baseFilterState = updatedFilterHistory['{}'];
        const mergedFilterState = [];
        const mergedFilterGroups = [];

        baseFilterState?.filterGroups?.forEach((filterGroup) => {
          const mergedGroupFilterState = [];
          filterGroup?.filters?.forEach((filter) => {
            const { id, selections } = filter;

            const resultFilter = results?.filterGroups?.find((resFilGrp) => resFilGrp.label === filterGroup.label)?.filters?.find((resFilter) => resFilter.id === id);
            const isSelected = requestBody?.filters && Object.hasOwnProperty.call(requestBody.filters, id);
            const partialFilterStateKey = { ...requestBody.filters };
            delete partialFilterStateKey[id];

            const partialFilterState = updatedFilterHistory[JSON.stringify(partialFilterStateKey) + requestBody.term]?.filterGroups?.find((fg) => fg.label === filterGroup.label)?.filters;

            if (resultFilter) {
              const mergedSelections = selections.map((selection) => {
                const returnedSelection = resultFilter?.selections?.find((resSelection) => resSelection.value === selection.value);
                // Return Selected items as they are returned from search same with any returned that have no selection at all in the filter
                if (returnedSelection && (returnedSelection.selected || !isSelected)) {
                  return returnedSelection;
                }
                // Filter is not included in the result set so all selections have a 0 count
                if (!isSelected) {
                  return { ...selection, count: 0 };
                }
                // Hide selection in current filter to see what sibling counts are
                if (partialFilterState) {
                  const pfsFilter = partialFilterState.find((fil) => fil.id === id);
                  if (pfsFilter && pfsFilter.selections) {
                    const relatedSelection = pfsFilter.selections.find((relSelection) => relSelection.value === selection.value);
                    if (relatedSelection) {
                      delete relatedSelection.selected;
                      return { ...relatedSelection };
                    }
                  }
                }
                return { ...selection, count: 0 };
              });
              mergedGroupFilterState.push({ ...filter, selections: mergedSelections });
            } else {
              // Filter was not returned at all in the results so there are no valid selections in this filter
              const disabledSelections = filter?.selections?.map((selection) => ({ ...selection, count: 0 }));
              mergedGroupFilterState.push({ ...filter, selections: disabledSelections });
            }
          });

          // Ensure that Microlearning and Courseware content cannot overlap in filters
          if (mergedGroupFilterState.filter((f) => f.id === 'deepdive' || f.id === 'microlearning').length > 0) {
            const ddFilter = mergedGroupFilterState.find((f) => f.id === 'deepdive');
            const mlFilter = mergedGroupFilterState.find((f) => f.id === 'microlearning');

            const mlHasSelections = mlFilter?.selections?.filter((f) => f.selected).length > 0;
            if (mlHasSelections) {
              ddFilter.selections = ddFilter.selections.map((s) => ({ ...s, selected: false, count: 0 }));
            }

            const ddHasSelections = ddFilter?.selections?.filter((f) => f.selected).length > 0;
            if (ddHasSelections) {
              mlFilter.selections = mlFilter.selections.map((s) => ({ ...s, selected: false, count: 0 }));
            }
          }

          mergedFilterGroups.push({ ...filterGroup, filters: mergedGroupFilterState });
        });
        baseFilterState?.filters?.forEach((filter) => {
          const { id, selections } = filter;

          const resultFilter = results?.filters?.find((resFilter) => resFilter.id === id);
          const isSelected = requestBody?.filters && Object.hasOwnProperty.call(requestBody.filters, id);
          const partialFilterStateKey = { ...requestBody.filters };
          delete partialFilterStateKey[id];

          const partialFilterState = updatedFilterHistory[JSON.stringify(partialFilterStateKey) + requestBody.term]?.filters;

          if (resultFilter) {
            const mergedSelections = selections.map((selection) => {
              const returnedSelection = resultFilter?.selections?.find((resSelection) => resSelection.value === selection.value);
              // Return Selected items as they are returned from search same with any returned that have no selection at all in the filter
              if (returnedSelection && (returnedSelection.selected || !isSelected)) {
                return returnedSelection;
              }
              // Filter is not included in the result set so all selections have a 0 count
              if (!isSelected) {
                return { ...selection, count: 0 };
              }
              // Hide selection in current filter to see what sibling counts are
              if (partialFilterState) {
                const pfsFilter = partialFilterState.find((fil) => fil.id === id);
                if (pfsFilter && pfsFilter.selections) {
                  const relatedSelection = pfsFilter.selections.find((relSelection) => relSelection.value === selection.value);
                  if (relatedSelection) {
                    delete relatedSelection.selected;
                    return { ...relatedSelection };
                  }
                }
              }
              return { ...selection, count: 0 };
            });
            mergedFilterState.push({ ...filter, selections: mergedSelections });
          } else {
            // Filter was not returned at all in the results so there are no valid selections in this filter
            const disabledSelections = filter?.selections?.map((selection) => ({ ...selection, count: 0 }));
            mergedFilterState.push({ ...filter, selections: disabledSelections });
          }
        });
        return [{ ...results, filters: mergedFilterState, filterGroups: mergedFilterGroups }, updatedFilterHistory];
      }

      return [results, updatedFilterHistory];
    }

    // Failure State
    return [
      {
        ...results,
        cards: [],
        filters: updatedFilterHistory['{}']?.filters || [],
        filterGroups: updatedFilterHistory['{}']?.filterGroups || [],
        totalResults: 0,
        term: requestBody.term ? decodeURIComponent(requestBody.term) : catalogResult.term,
        page: 1,
      },
      updatedFilterHistory,
    ];
  });

  // Map page data onto each card
  logger.info('Catalog - Mapping data on cards');
  if (data[0].cards) {
    data[0].cards = data[0].cards.map((card) => ({ ...card, page: data.page }));
  }
  const endTime = new Date();
  data[0].callTime = Math.round((endTime.getTime() - startTime.getTime()) / 10) / 100;
  logger.info('Catalog - returning data');
  return data;
};

export const getStaticContent = ({
  slugs, env, isPreview, isSSR = false,
}) => {
  const host = getCourseAPIHost({ env, isPreview, isSSR });
  const dataFetchURL = `${host}${getEnvironmentConstant({ key: ROUTER_BASENAME, env })}/api/edc/v1`;
  if (isSSR) {
    logger.debug('DEBUG: getStaticContent:', host, dataFetchURL);
  }
  const dataPromiseStart = new Date().getTime();

  let dataPromiseTime;
  const dataPromise = new Promise((resolve, reject) => {
    const dataTimer = new ApiTimer();
    dataTimer.set(dataFetchURL);

    axios
      .post(dataFetchURL, { slugs }, { headers: { ...headers } })
      .catch((err) => {
        dataTimer.clear();
        const xhrErr = new XHRError(err?.response?.status || 500, err.message);
        logger.error(`Error in getStaticContent (catch1) calling: ${dataFetchURL}, ${isSSR ? 'isSSR: true' : 'isSSR: false'}, ${err}`);
        reject(xhrErr);
      })
      .then((payload) => {
        dataTimer.clear();
        dataPromiseTime = new Date().getTime() - dataPromiseStart;
        logger.debug(`Timing:\ngetStaticContent call: ${dataPromiseTime}ms`);

        resolve({ ...payload?.data });
      })

      .catch((err) => {
        dataTimer.clear();
        logger.error(`Error in getStaticContent calling: ${dataFetchURL}, ${isSSR ? 'isSSR: true' : 'isSSR: false'}, (catch2) ${err}`);
        reject(err);
      });
  });
  return dataPromise;
};

export const decorateListData = ({
  env, isPreview, listArray, appVersion,
}) => {
  const host = getCourseAPIHost({ env, isPreview });
  let listCourseArray = [];
  listArray.forEach((list) => {
    if (list.listId !== LIST_ID_FOLLOWED) { // Handle regular lists
      list?.listItems?.forEach((listItem) => { listCourseArray.push(listItem?.resourceId?.split('/')?.pop()); });
    } else { // Handle followed lists which is a list of lists
      list?.listItems?.forEach((followedList) => {
        followedList?.listItems?.forEach(
          (listItem) => {
            listCourseArray.push(listItem?.resourceId?.split('/')?.pop());
          },
        );
      });
    }
  });

  // Dedupe the list
  listCourseArray = [...new Set(listCourseArray)];

  if (listCourseArray.length) {
    const chunkedListArray = chunkArray(listCourseArray, 50);

    const contextHeaders = {};
    const contextChannel = getEnvironmentConstant({ key: CLIENT_CONTEXT_CHANNEL, env });
    if (contextChannel) {
      contextHeaders['x-client-context-channel'] = contextChannel;
    }

    // Fetch CMS data for courses in each list
    // eslint-disable-next-line max-len
    const serviceRequestsArray = chunkedListArray.map((listArrayChunk) => axios.post(`${host}${getEnvironmentConstant({ key: ROUTER_BASENAME, env })}/api/listCourses/v1/`, listArrayChunk, { headers: { 'X-GnosisVersion': appVersion, ...contextHeaders } }).then((courseData) => listArray.map((list) => {
      if (list.listId !== LIST_ID_FOLLOWED) { // Handle regular lists
        return {
          ...list,
          listItems: list.listItems.map((listItem) => {
            const data = courseData?.data?.find((course) => course.id === listItem.resourceId.split('/')
              .pop());
            if (!data) {
              return null;
            }
            return {
              ...listItem,
              type: listItem.type || OBJECT_TYPE_COURSE,
              data,
            };
          }).filter((listItem) => listItem !== null),
        };
      }
      return { // Handle followed lists which is a list of lists
        ...list,
        listItems: list?.listItems?.map((followedList) => ({
          ...followedList,
          listItems: followedList?.listItems?.map((listItem) => {
            const data = courseData.data.find((course) => course.id === listItem.resourceId.split('/')
              .pop());
            if (!data) {
              return null;
            }
            return {
              ...listItem,
              data,
            };
          }).filter((listItem) => listItem !== null),
        })),
      };
    })));

    // Return multiple arrays of data flattened into a single array
    return Promise.all(serviceRequestsArray).then((arraysOfListData) => flatDeep(arraysOfListData, 1));
  }
  return Promise.resolve(listArray);
};

export const fetchListById = async ({
  listId, oa2Token, env, isPreview = false, appVersion,
// eslint-disable-next-line consistent-return
}) => {
  if (!oa2Token) {
    return Promise.reject();
  }

  // Call service
  const serviceURL = `${getEnvironmentConstant({ env, key: LIST_API_HOST })}/lists/${listId}?targetChannels=${getEnvironmentConstant({ key: CLIENT_CONTEXT_CHANNEL, env })}`;
  logger.info(`Calling fetchListById: ${serviceURL}`);

  return axios.get(serviceURL, {
    headers: {
      Authorization: `Bearer ${oa2Token}`,
      'X-GnosisVersion': appVersion,
    },
  }).then((listData) => {
    if (listData?.data?.items && listData?.data?.items[0].listId) {
      return decorateListData({
        env, isPreview, listArray: [listData?.data?.items[0]], appVersion,
      });
    }
    return Promise.reject(listData);
  });
};

export const getNavData = async ({
  env, isPreview, isSSR,
}) => {
  const host = getCourseAPIHost({ env, isPreview, isSSR });
  const dataFetchURL = `${host}${getEnvironmentConstant({ key: ROUTER_BASENAME, env })}/api/nav/v1`;
  if (isSSR) {
    logger.debug('DEBUG: getNavData:', host, dataFetchURL);
  }
  const dataPromiseStart = new Date().getTime();

  let dataPromiseTime;
  const dataPromise = new Promise((resolve, reject) => {
    const dataTimer = new ApiTimer();
    dataTimer.set(dataFetchURL);

    axios
      .get(dataFetchURL, { headers: { ...headers } })
      .catch((err) => {
        dataTimer.clear();
        const xhrErr = new XHRError(err?.response?.status || 500, err.message);
        logger.error(`Error in getNavData (catch1) calling: ${dataFetchURL}, ${isSSR ? 'isSSR: true' : 'isSSR: false'}, ${err}`);
        reject(xhrErr);
      })
      .then((payload) => {
        dataTimer.clear();
        dataPromiseTime = new Date().getTime() - dataPromiseStart;
        logger.debug(`Timing:\ngetNavData call: ${dataPromiseTime}ms`);

        resolve({ ...payload?.data });
      })

      .catch((err) => {
        dataTimer.clear();
        logger.error(`Error in getNavData calling: ${dataFetchURL}, ${isSSR ? 'isSSR: true' : 'isSSR: false'}, (catch2) ${err}`);
        reject(err);
      });
  });
  return dataPromise;
};

export const getAvailableProducts = ({ env, appVersion }) => axios.post(getEnvironmentConstant({ key: FILTER_API, env }), { source: 'Learn ONE' }, { headers: { accept: 'application/json', 'X-GnosisVersion': appVersion } })
  .catch((err) => {
    logger.error(`Index - Filter API call failed for ${getEnvironmentConstant({ key: FILTER_API, env })} - ${safeStringify(err)}`);
    return {};
  }).then((res) => ({
    productList: getChildProperty(res, 'data.filterGroups[0].filters[0].selections') || [],
    industrySubSegments: getChildProperty(res, 'data.filterGroups[0].filters[1].selections') || [],
  }));

export const getEnrollmentData = ({
  params, env, isPreview, apiVersion = 'v1',
}) => {
  const { classId } = params;
  const host = getCourseAPIHost({ env, isPreview });
  const classDataUrl = `${host}${getEnvironmentConstant({ key: ROUTER_BASENAME, env })}/api/liveclass/${apiVersion}`;
  const classDataPromise = new Promise((resolve, reject) => {
    axios.post(classDataUrl, { classId })
      .then((res) => {
        const data = res?.data || {};
        const slugs = getEnrollmentSlug({ locale: data?.localeOfClass });

        getStaticContent({
          env, isPreview, slugs,
        }).then((staticContent) => {
          // Booleans is isLoaderVisible,
          resolve([data, staticContent, false]);
        }).catch((err) => reject(err));
      }).catch((err) => { reject(err); });
  });

  return classDataPromise;
};

export const getCoachingCatalogResult = async ({
  catalogResult, presetFilterString, env, appVersion, perPage = 24, filterHistory = {},
}) => {
  // For coaching catalog, we use the same structure as regular catalog
  // but the actual data fetching is handled by the coaching catalog slice
  // This function is mainly for SSR compatibility

  logger.info('getCoachingCatalogResult called for SSR', {
    catalogResult,
    presetFilterString,
    env,
    appVersion,
    perPage,
  });

  // Return a basic structure that will be populated by the client-side slice
  return [{
    status: 'SSR_Placeholder',
    cards: [],
    filters: [],
    filterGroups: [],
    totalResults: 0,
    page: catalogResult?.page || 1,
    term: catalogResult?.term || '',
    sort: catalogResult?.sort || 'relevance',
  }, filterHistory];
};

export const initialState = {};

initialState[catalogSliceName] = catalogSliceInitialState;
